/**
 * Tahap 6C — FMCG Inventory Control, FEFO, Expiry, Reorder & Management Dashboard.
 * Bahasa Indonesia · konsisten dengan tema slate.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import { Bell, Package, TrendingDown, ClipboardList, ShieldCheck, RefreshCw } from "lucide-react";
import { NoAccessBanner } from "./InventoryPages";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INV_PERMS_BY_ROLE = {
  super_admin: ["*"], gudang: ["INV_LIHAT", "INV_BATCH", "INV_TRANSAKSI"],
  akuntansi: ["INV_LIHAT", "INV_INTEGRITY", "INV_HPP_LIHAT"],
  keuangan: ["INV_LIHAT"], auditor: ["INV_LIHAT", "INV_INTEGRITY", "INV_BATCH"],
  direktur_utama: ["INV_LIHAT", "INV_INTEGRITY", "INV_BATCH"],
  direktur_operasional: ["INV_LIHAT"], perpajakan: ["INV_LIHAT"], viewer: ["INV_LIHAT"],
};
function useCan() {
  const { user } = useAuth();
  const perms = INV_PERMS_BY_ROLE[user?.role] || [];
  const isSuper = user?.role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

function Crumb({ path }) {
  return <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{path.map((p, i) => (<span key={i}>{p}{i < path.length - 1 && " › "}</span>))}</div>;
}
function Stat({ label, value, tone = "slate", sub, testid }) {
  const map = { emerald: "bg-emerald-50 border-emerald-200", sky: "bg-sky-50 border-sky-200", amber: "bg-amber-50 border-amber-200", rose: "bg-rose-50 border-rose-200", slate: "bg-slate-50 border-slate-200", blue: "bg-blue-50 border-blue-200" };
  return <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={testid}>
    <div className="text-xs opacity-80">{label}</div>
    <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
    {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
  </div>;
}
const bktCls = (s) => s === "SESUAI" ? "bg-emerald-600" : s === "ERROR" ? "bg-rose-600" : s === "PERLU_DITINJAU" ? "bg-amber-500" : "bg-slate-500";
const statCls = (s) => ({ AMAN: "bg-emerald-600", REORDER: "bg-amber-500", KRITIS: "bg-rose-600",
  "MENDEKATI KEDALUWARSA": "bg-amber-500", KEDALUWARSA: "bg-rose-700",
  FAST_MOVING: "bg-emerald-600", NORMAL: "bg-sky-500", SLOW_MOVING: "bg-amber-500", DEAD_STOCK: "bg-rose-700",
  CRITICAL: "bg-rose-700", WARNING: "bg-amber-500", INFO: "bg-sky-500", ACTIVE: "bg-amber-500",
  ACKNOWLEDGED: "bg-slate-500" }[s] || "bg-slate-500");

/* ============ 6C Executive Dashboard ============ */
export function ManagementDashboard() {
  const can = useCan();
  const canView = can("INV_LIHAT");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!canView) return;
    api.get("/inventory/6c/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [canView]);
  if (!canView) return <div className="space-y-6 page-fade" data-testid="inv6c-dash-page"><h1 className="font-display text-3xl font-bold">Dasbor Manajemen Persediaan</h1><NoAccessBanner perm="INV_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="inv6c-dash-page">
      <div>
        <Crumb path={["Persediaan", "Dasbor Manajemen (6C)"]} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dasbor Manajemen Persediaan</h1>
        <p className="text-sm text-slate-500 mt-1">Executive view: nilai persediaan, aging, kedaluwarsa, reorder, top produk, margin.</p>
      </div>
      {d && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Nilai Persediaan" value={formatRupiah(d.total_inventory_value)} tone="emerald" testid="inv6c-stat-value" />
          <Stat label="SKU Perlu Reorder" value={d.reorder.reorder + d.reorder.kritis} sub={`${d.reorder.kritis} kritis`} tone={d.reorder.kritis > 0 ? "rose" : "amber"} testid="inv6c-stat-reorder" />
          <Stat label="Batch Near/Expired" value={(d.expiry_buckets["EXPIRED"] || 0) + (d.expiry_buckets["H-7"] || 0) + (d.expiry_buckets["H-14"] || 0) + (d.expiry_buckets["H-30"] || 0)} sub={`Nilai ${formatRupiah(d.expiry_total_value)}`} tone="amber" testid="inv6c-stat-expiry" />
          <Stat label="Dead / Slow Stock" value={d.dead_stock_count + d.slow_moving_count} sub={`${d.dead_stock_count} dead · ${d.slow_moving_count} slow`} tone="rose" testid="inv6c-stat-dead" />
        </div>
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2">Stok per Gudang</h3>
          <Table><TableHeader><TableRow><TableHead>Gudang</TableHead><TableHead>Kode</TableHead><TableHead>Qty</TableHead><TableHead>Nilai</TableHead></TableRow></TableHeader><TableBody>
            {(d.warehouses || []).map((w, i) => <TableRow key={i}><TableCell>{w.name}</TableCell><TableCell>{w.code}</TableCell><TableCell className="font-mono-num">{w.qty}</TableCell><TableCell className="font-mono-num">{formatRupiah(w.value)}</TableCell></TableRow>)}
          </TableBody></Table>
        </CardContent></Card>
        <div className="grid md:grid-cols-2 gap-4">
          <Card><CardContent className="p-4">
            <h3 className="font-semibold mb-2">Top 5 Produk by Sales</h3>
            <Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>Net Sales</TableHead><TableHead>GP</TableHead></TableRow></TableHeader><TableBody>
              {(d.top_by_sales || []).map((r, i) => <TableRow key={i}><TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell><TableCell className="font-mono-num">{formatRupiah(r.net_sales)}</TableCell><TableCell className="font-mono-num">{formatRupiah(r.gross_profit)}</TableCell></TableRow>)}
              {(d.top_by_sales || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <h3 className="font-semibold mb-2">Top 5 Produk by Gross Profit</h3>
            <Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>GP</TableHead><TableHead>GM %</TableHead></TableRow></TableHeader><TableBody>
              {(d.top_by_gross_profit || []).map((r, i) => <TableRow key={i}><TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell><TableCell className="font-mono-num">{formatRupiah(r.gross_profit)}</TableCell><TableCell className="font-mono-num">{r.gross_margin_pct}%</TableCell></TableRow>)}
              {(d.top_by_gross_profit || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </div>
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2">Aging Persediaan (Nilai)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(d.aging_buckets || {}).map(([k, v]) => <Stat key={k} label={k + " hari"} value={formatRupiah(v)} tone={k === ">180" ? "rose" : k === "91-180" ? "amber" : "slate"} testid={`inv6c-aging-${k}`} />)}
          </div>
        </CardContent></Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ["/persediaan/6c/expiry", "Monitoring Kedaluwarsa", ClipboardList],
            ["/persediaan/6c/reorder", "Reorder Point", RefreshCw],
            ["/persediaan/6c/aging", "Aging & Slow Moving", TrendingDown],
            ["/persediaan/6c/margin", "Margin per Produk", Package],
            ["/persediaan/6c/notifikasi", "Notifikasi Persediaan", Bell],
            ["/persediaan/6c/integritas", "Integritas 6C", ShieldCheck],
          ].map(([p, t, I]) => <Card key={p} className="hover:border-slate-400"><CardContent className="p-4">
            <div className="flex items-center gap-2"><I className="h-5 w-5 text-slate-700" /><div className="font-semibold">{t}</div></div>
            <Link to={p}><Button size="sm" variant="outline" className="mt-3" data-testid={`inv6c-nav-${p.split('/').pop()}`}>Buka</Button></Link>
          </CardContent></Card>)}
        </div>
      </>)}
    </div>
  );
}

/* ============ Expiry Page ============ */
export function ExpiryPage() {
  const can = useCan();
  const canView = can("INV_BATCH");
  const [d, setD] = useState(null);
  const [bkt, setBkt] = useState("");
  useEffect(() => {
    if (!canView) return;
    api.get(`/inventory/6c/expiry${bkt ? "?days_bucket=" + bkt : ""}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [canView, bkt]);
  if (!canView) return <div className="space-y-6" data-testid="inv6c-expiry-page"><h1 className="font-display text-3xl font-bold">Monitoring Kedaluwarsa</h1><NoAccessBanner perm="INV_BATCH" /></div>;
  return <div className="space-y-6 page-fade" data-testid="inv6c-expiry-page">
    <div><Crumb path={["Persediaan", "Monitoring Kedaluwarsa"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold">Monitoring Kedaluwarsa</h1></div>
    <div className="flex gap-2 flex-wrap">
      {["", "EXPIRED", "H-7", "H-14", "H-30", "H-60", "H-90", "AMAN"].map(b => <Button key={b || "ALL"} size="sm" variant={bkt === b ? "default" : "outline"} onClick={() => setBkt(b)} data-testid={`inv6c-exp-filter-${b || "all"}`}>{b || "SEMUA"}{d && b ? ` (${d.buckets?.[b] || 0})` : ""}</Button>)}
    </div>
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Qty</TableHead><TableHead>Nilai</TableHead><TableHead>Kedaluwarsa</TableHead><TableHead>Sisa Hari</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
      <TableBody>
        {d?.rows?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {(d?.rows || []).map((r, i) => <TableRow key={i} data-testid={`inv6c-exp-row-${i}`}>
          <TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell>
          <TableCell>{r.warehouse_code}</TableCell><TableCell>{r.batch_no}</TableCell>
          <TableCell className="font-mono-num">{r.qty}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.value)}</TableCell>
          <TableCell>{formatDateID(r.expiry_date)}</TableCell>
          <TableCell className="font-mono-num">{r.days_remaining}</TableCell>
          <TableCell><Badge className={statCls(r.status)}>{r.status}</Badge></TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
    {d && <div className="text-xs text-slate-500">Total nilai kritis+near: {formatRupiah(d.totals.total_value)} · Digenerate {formatDateID(d.generated_at)}</div>}
  </div>;
}

/* ============ Reorder Page ============ */
export function ReorderPage() {
  const can = useCan();
  const canView = can("INV_LIHAT");
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const [poRow, setPoRow] = useState(null);
  useEffect(() => {
    if (!canView) return;
    api.get("/inventory/6c/reorder").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [canView]);
  if (!canView) return <div><NoAccessBanner perm="INV_LIHAT" /></div>;
  const buatDraftPO = async (r, sup) => {
    try {
      const body = {
        date: new Date().toISOString().slice(0, 10),
        party_code: sup.code, party_name: sup.name,
        warehouse_code: "",
        currency: "IDR",
        reference_number: `PO-REORDER-${r.sku}`,
        lines: [{ product_sku: r.sku, qty: r.recommended_qty || 0, price: 0 }],
        source: "reorder_recommendation",
        source_sku: r.sku,
      };
      const { data } = await api.post("/txn/entries/purchase_order", body);
      toast.success(`Draft PO ${data.number} dibuat dari rekomendasi reorder. Silakan lengkapi harga.`);
      navigate(`/pembelian/po?highlight=${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-6 page-fade" data-testid="inv6c-reorder-page">
    <div><Crumb path={["Persediaan", "Reorder Point"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold">Analisis Reorder Point</h1></div>
    {d && <div className="grid grid-cols-3 gap-3">
      <Stat label="AMAN" value={d.summary.aman} tone="emerald" testid="inv6c-ro-aman" />
      <Stat label="REORDER" value={d.summary.reorder} tone="amber" testid="inv6c-ro-reorder" />
      <Stat label="KRITIS" value={d.summary.kritis} tone="rose" testid="inv6c-ro-kritis" />
    </div>}
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>Tersedia</TableHead><TableHead>Min</TableHead><TableHead>Reorder Pt</TableHead><TableHead>Safety</TableHead><TableHead>Qty Rekomendasi</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
      <TableBody>
        {d?.rows?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">TIDAK ADA DATA — belum ada SKU dengan min_stock/reorder_point tersetel.</TableCell></TableRow>}
        {(d?.rows || []).map((r, i) => <TableRow key={i} data-testid={`inv6c-ro-row-${i}`}>
          <TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell>
          <TableCell className="font-mono-num">{r.available_qty}</TableCell>
          <TableCell className="font-mono-num">{r.min_stock}</TableCell>
          <TableCell className="font-mono-num">{r.reorder_point}</TableCell>
          <TableCell className="font-mono-num">{r.safety_stock}</TableCell>
          <TableCell className="font-mono-num font-semibold">{r.recommended_qty}</TableCell>
          <TableCell><Badge className={statCls(r.status)}>{r.status}</Badge></TableCell>
          <TableCell>{(r.status === "REORDER" || r.status === "KRITIS") && r.recommended_qty > 0 &&
            <Button size="sm" variant="outline" onClick={() => setPoRow(r)} data-testid={`inv6c-ro-po-${i}`}>Buat Draft PO</Button>}</TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
    <SupplierPickDialog row={poRow} onClose={() => setPoRow(null)} onPick={(sup) => { const r = poRow; setPoRow(null); buatDraftPO(r, sup); }} />
    <p className="text-xs text-slate-500">Rekomendasi bersifat saran; PO Draft dibuka pada modul Pembelian dan harus dilengkapi supplier + harga sebelum di-approve.</p>
  </div>;
}

function SupplierPickDialog({ row, onClose, onPick }) {
  const [sups, setSups] = useState([]);
  const [code, setCode] = useState("");
  useEffect(() => {
    if (!row) return;
    setCode("");
    api.get("/masters/suppliers").then(({ data }) => setSups(data.filter((s) => s.status === "ACTIVE" && s.code))).catch((e) => toast.error(formatApiError(e)));
  }, [row]);
  if (!row) return null;
  return <Dialog open onOpenChange={onClose}><DialogContent data-testid="inv6c-ro-supplier-dialog">
    <DialogHeader><DialogTitle>Pilih Pemasok untuk Draft PO {row.sku}</DialogTitle></DialogHeader>
    <p className="text-sm text-slate-500">Nomor PO memakai singkatan pemasok, sehingga pemasok wajib dipilih.</p>
    <Select value={code} onValueChange={setCode}><SelectTrigger data-testid="inv6c-ro-supplier-select"><SelectValue placeholder="Pilih pemasok" /></SelectTrigger>
      <SelectContent>{sups.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} — {s.name}</SelectItem>)}</SelectContent></Select>
    <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button>
      <Button disabled={!code} onClick={() => onPick(sups.find((s) => s.code === code))} data-testid="inv6c-ro-supplier-confirm">Buat Draft PO</Button></DialogFooter>
  </DialogContent></Dialog>;
}

/* ============ Aging Page ============ */
export function AgingPage() {
  const can = useCan();
  const canView = can("INV_LIHAT");
  const [d, setD] = useState(null);
  useEffect(() => { if (canView) api.get("/inventory/6c/aging").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, [canView]);
  if (!canView) return <div><NoAccessBanner perm="INV_LIHAT" /></div>;
  return <div className="space-y-6 page-fade" data-testid="inv6c-aging-page">
    <div><Crumb path={["Persediaan", "Aging & Slow Moving"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold">Aging Persediaan & Klasifikasi Pergerakan</h1></div>
    {d && <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {Object.entries(d.buckets).map(([k, v]) => <Stat key={k} label={k + " hari"} value={formatRupiah(v)} tone={k === ">180" ? "rose" : k === "91-180" ? "amber" : "slate"} testid={`inv6c-aging-bkt-${k}`} />)}
    </div>}
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>Gudang</TableHead><TableHead>Batch</TableHead><TableHead>Qty</TableHead><TableHead>Nilai</TableHead><TableHead>Umur</TableHead><TableHead>Sales 30d</TableHead><TableHead>Klasifikasi</TableHead></TableRow></TableHeader>
      <TableBody>
        {d?.rows?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {(d?.rows || []).map((r, i) => <TableRow key={i} data-testid={`inv6c-aging-row-${i}`}>
          <TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell>
          <TableCell>{r.warehouse_code}</TableCell><TableCell>{r.batch_no}</TableCell>
          <TableCell className="font-mono-num">{r.qty}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.value)}</TableCell>
          <TableCell className="font-mono-num">{r.age_days}</TableCell>
          <TableCell className="font-mono-num">{r.sales_qty_30d}</TableCell>
          <TableCell><Badge className={statCls(r.classification)}>{r.classification}</Badge></TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Margin per Produk ============ */
export function MarginPage() {
  const can = useCan();
  const canView = can("INV_LIHAT");
  const [d, setD] = useState(null);
  const [f, setF] = useState({ year: "", month: "" });
  const load = async () => {
    if (!canView) return;
    try {
      const qs = [f.year ? `year=${f.year}` : "", f.month ? `month=${f.month}` : ""].filter(Boolean).join("&");
      const { data } = await api.get(`/inventory/6c/margin${qs ? "?" + qs : ""}`);
      setD(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [canView]);
  if (!canView) return <div><NoAccessBanner perm="INV_LIHAT" /></div>;
  return <div className="space-y-6 page-fade" data-testid="inv6c-margin-page">
    <div><Crumb path={["Persediaan", "Margin per Produk"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold">Margin per Produk (Historical HPP)</h1></div>
    <Card><CardContent className="p-4 flex gap-3 items-end flex-wrap">
      <div><Label>Tahun</Label><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} data-testid="inv6c-margin-year" className="w-32" placeholder="mis. 2026" /></div>
      <div><Label>Bulan (opsional)</Label><Input type="number" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} data-testid="inv6c-margin-month" className="w-24" placeholder="1-12" /></div>
      <Button onClick={load} data-testid="inv6c-margin-apply"><RefreshCw className="h-4 w-4 mr-2" />Terapkan</Button>
    </CardContent></Card>
    {d?.totals && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Penjualan Bruto" value={formatRupiah(d.totals.gross_sales)} tone="sky" testid="inv6c-mtot-gs" />
      <Stat label="Net Sales" value={formatRupiah(d.totals.net_sales)} tone="emerald" testid="inv6c-mtot-net" />
      <Stat label="Historical HPP" value={formatRupiah(d.totals.historical_hpp)} tone="amber" testid="inv6c-mtot-hpp" />
      <Stat label="Gross Profit" value={formatRupiah(d.totals.gross_profit)} sub={`${d.totals.gross_margin_pct}%`} tone="blue" testid="inv6c-mtot-gp" />
    </div>}
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Produk</TableHead><TableHead>Qty</TableHead><TableHead>Bruto</TableHead><TableHead>Diskon</TableHead><TableHead>Retur</TableHead><TableHead>Net Sales</TableHead><TableHead>HPP</TableHead><TableHead>GP</TableHead><TableHead>GM%</TableHead></TableRow></TableHeader>
      <TableBody>
        {d?.rows?.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {(d?.rows || []).map((r, i) => <TableRow key={i} data-testid={`inv6c-margin-row-${i}`}>
          <TableCell>{r.sku}</TableCell><TableCell>{r.product_name}</TableCell>
          <TableCell className="font-mono-num">{r.qty_sold}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.gross_sales)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.discount)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.returns)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.net_sales)}</TableCell>
          <TableCell className="font-mono-num">{formatRupiah(r.historical_hpp)}</TableCell>
          <TableCell className="font-mono-num font-semibold">{formatRupiah(r.gross_profit)}</TableCell>
          <TableCell className="font-mono-num">{r.gross_margin_pct}%</TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Notification Center ============ */
export function NotificationCenter() {
  const can = useCan();
  const canView = can("INV_LIHAT");
  const canGen = can("INV_INTEGRITY");
  const [rows, setRows] = useState([]);
  const [filt, setFilt] = useState({ status: "ACTIVE", category: "" });
  const load = async () => {
    if (!canView) return;
    try {
      const qs = [`status=${filt.status}`, filt.category ? `category=${filt.category}` : ""].filter(Boolean).join("&");
      const { data } = await api.get(`/inventory/6c/notifications?${qs}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [canView, filt.status, filt.category]);
  const generate = async () => {
    try { const { data } = await api.post("/inventory/6c/notifications/generate"); toast.success(`${data.created} notifikasi baru`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ack = async (id) => {
    try { await api.post(`/inventory/6c/notifications/${id}/acknowledge`); toast.success("Ditandai selesai"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div><NoAccessBanner perm="INV_LIHAT" /></div>;
  return <div className="space-y-6 page-fade" data-testid="inv6c-notif-page">
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div><Crumb path={["Persediaan", "Notifikasi"]} /><h1 className="font-display text-3xl sm:text-4xl font-bold">Notifikasi Persediaan</h1></div>
      {canGen && <Button onClick={generate} data-testid="inv6c-notif-gen"><Bell className="h-4 w-4 mr-2" />Generate Notifikasi</Button>}
    </div>
    <div className="flex gap-2 flex-wrap">
      {["ACTIVE", "ACKNOWLEDGED"].map(s => <Button key={s} size="sm" variant={filt.status === s ? "default" : "outline"} onClick={() => setFilt({ ...filt, status: s })} data-testid={`inv6c-notif-status-${s}`}>{s}</Button>)}
      {["", "EXPIRY", "REORDER", "DEAD_STOCK"].map(c => <Button key={c || "ALL"} size="sm" variant={filt.category === c ? "default" : "outline"} onClick={() => setFilt({ ...filt, category: c })} data-testid={`inv6c-notif-cat-${c || "all"}`}>{c || "SEMUA"}</Button>)}
    </div>
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Kategori</TableHead><TableHead>Judul</TableHead><TableHead>Severity</TableHead><TableHead>Dibuat</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
        {rows.map((n, i) => <TableRow key={n.id} data-testid={`inv6c-notif-row-${i}`}>
          <TableCell><Badge className="bg-slate-500">{n.category}</Badge></TableCell>
          <TableCell>{n.title}</TableCell>
          <TableCell><Badge className={statCls(n.severity)}>{n.severity}</Badge></TableCell>
          <TableCell className="text-xs">{formatDateID(n.created_at)}</TableCell>
          <TableCell>{n.status === "ACTIVE" && canView && <Button size="sm" variant="outline" onClick={() => ack(n.id)} data-testid={`inv6c-notif-ack-${n.id}`}>Tandai Selesai</Button>}
            {n.status === "ACKNOWLEDGED" && <Badge className="bg-slate-500">Selesai</Badge>}</TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}

/* ============ Integrity 6C ============ */
export function Integrity6CPage() {
  const can = useCan();
  const canView = can("INV_INTEGRITY");
  const [d, setD] = useState(null);
  useEffect(() => { if (canView) api.get("/inventory/6c/integrity").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, [canView]);
  if (!canView) return <div className="space-y-6" data-testid="inv6c-integrity-page"><h1 className="font-display text-3xl font-bold">Integritas Kontrol Persediaan (6C)</h1><NoAccessBanner perm="INV_INTEGRITY" /></div>;
  return <div className="space-y-6 page-fade" data-testid="inv6c-integrity-page">
    <div><Crumb path={["Persediaan", "Integritas 6C"]} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Integritas Kontrol Persediaan (6C)</h1>
        {d && <Badge className={bktCls(d.overall)} data-testid="inv6c-int-overall">{d.overall}</Badge>}
      </div>
      <p className="text-sm text-slate-500 mt-2">10 cek: Batch↔Ledger, Batch Warehouse, Penjualan setelah expired, FEFO, Aging↔Ledger, Nilai vs GL, HPP vs GL, Batch Transfer, Disposal, Duplicate Notification.</p>
    </div>
    <Card><CardContent className="p-4"><Table>
      <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Cek</TableHead><TableHead>Ekspektasi</TableHead><TableHead>Aktual</TableHead><TableHead>Selisih / Mismatch</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
      <TableBody>
        {(d?.checks || []).map((c, i) => <TableRow key={i} data-testid={`inv6c-int-row-${i}`}>
          <TableCell>{i + 1}</TableCell><TableCell>{c.name}</TableCell>
          <TableCell className="font-mono-num">{c.expected !== undefined ? c.expected : "-"}</TableCell>
          <TableCell className="font-mono-num">{c.actual !== undefined ? c.actual : "-"}</TableCell>
          <TableCell className="font-mono-num">{c.mismatch_count !== undefined ? `${c.mismatch_count}×` : (c.difference !== undefined ? c.difference : "-")}</TableCell>
          <TableCell><Badge className={bktCls(c.status)}>{c.status}</Badge></TableCell>
        </TableRow>)}
      </TableBody>
    </Table></CardContent></Card>
  </div>;
}
